import { deleteRequest, putRequest } from 'apis/fetcher'
import { makeAutoObservable } from 'mobx'
import {
  CareerTypeId,
  DashboardPosition,
  DashboardPositionAdvertisementsParams,
  DashboardPositionParams,
  StartupPositionRemoteWorkType,
  StartupPositionStockOptionType,
  Envelope,
  PositionType,
  Range,
  TechStack,
  WithIdName,
  WithIdNameParentId,
} from 'types/common'
import {
  DASHBOARD_POSITION_FORM_SALARY_SECTION_ID,
  DASHBOARD_POSITION_SALARY_VALIDATION_ERROR_MESSAGE,
  DEFAULT_MAX_SALARY,
  TEXT_EDITOR_DEFAULT_VALUE,
} from 'infra/constants'
import { mutate } from 'swr'
import { customToast, requestToast } from 'components/common/toast'
import {
  validateDashboardPosition,
  checkEditorDefaultValue,
} from 'utils/validations'
import { findIndex } from 'lodash-es'
import {
  postDashboardPositionAdvertisements,
  postRequestDashboardPositions,
  postRequestDashboardScoutTalentRecommendRequest,
  putRequestDashboardPositions,
} from 'apis/request'
import { noticePrivateInfoDetected, scrollIntoView } from 'utils/handlers'

const initialPosition: DashboardPosition = {
  id: 0,
  name: '',
  positionTypes: [],
  careerType: null,
  experienceRange: { min: 0, max: 10 },
  techStacks: [],
  hasSupervisor: false,
  task: TEXT_EDITOR_DEFAULT_VALUE,
  qualification: TEXT_EDITOR_DEFAULT_VALUE,
  preferred: TEXT_EDITOR_DEFAULT_VALUE,
  hiringProcess: TEXT_EDITOR_DEFAULT_VALUE,
  expectationKeywords: [],
  isPublished: false,
  publishedAt: null,
  applicationCount: 0,
  createdAt: '',
  talentRecommendRequestedAt: null,
  jobInterestLevel: null,
  location: null,
  address: '',
  minDesiredSalary: 0,
  maxDesiredSalary: 0,
  maxNegotiableSalary: 0,
  internPeriod: null,
  stockOption: null,
  remoteWorkPreference: null,
  isSameLocation: null,
}

const DELETE_ALERT_TEXT = `이 채용 공고에 지원한 인재를 더이상 볼 수 없어요.\n정말 삭제하시겠어요?\n(서류합격 후 채팅 중인 인재는 채팅 목록에 표시돼요)`

export class DashboardPositionStore {
  position = initialPosition
  hasSalaryUnknownOption: boolean = false // "예상 연봉을 알 수 없어요" 옵션이 있는지
  isSalaryUnknown: boolean = false // "예상 연봉을 알 수 없어요" 여부
  isCreating: boolean = true // 새 채용 공고 만드는 중인 상태 체크
  hasChanged: boolean = false // 데이터 수정내역 체크
  isOpenLockModal: boolean = false
  isMobilePositionListView: boolean = true // 모바일에서 리스트 화면인지 확인
  isOpenTossModal: boolean = false
  isOpenAdModal: boolean = false
  isOpenPositionPreviewModal: boolean = false
  isOpenAttentionTipBox: boolean = false
  positionToPublish: DashboardPosition | undefined = undefined // 채용 공고 공개 모달을 띄웠을 때 타겟 공고가 현재 화면으로 보고 있는 것과 다를 때

  constructor(position?: DashboardPosition) {
    if (position) this.positionBoxSelectHandler(position)
    makeAutoObservable(this)
  }

  get techStacksId() {
    return this.position.techStacks.map((item) => item.id)
  }

  get confirmDataChanged() {
    return (
      this.hasChanged &&
      !window.confirm('공고 내용을 저장하지 않으셨습니다.\n이동하시겠습니까?')
    )
  }

  get getPosition() {
    return this.positionToPublish || this.position
  }

  get optional() {
    return [
      {
        name: '채용절차',
        isChecked:
          !!this.position.hiringProcess &&
          !checkEditorDefaultValue(this.position.hiringProcess) &&
          this.position.hiringProcess.length >= 20,
      },
      {
        name: '합류 시 제공할 수 있는 것',
        isChecked: !!this.position.expectationKeywords.length,
      },
    ]
  }

  get requirement() {
    return [
      { name: '채용 공고 제목', isChecked: !!this.position.name },
      {
        name: '포지션',
        isChecked: !!this.position.positionTypes.length,
      },
      {
        name: '경력',
        isChecked:
          !!this.position.careerType &&
          (this.position.careerType.id === CareerTypeId.INTERN
            ? !!this.position.internPeriod && !!this.position.jobInterestLevel
            : true),
      },
      {
        name: '스킬',
        isChecked: !!this.position.techStacks.length,
      },
      {
        name: '주요업무',
        isChecked:
          !!this.position.task &&
          !checkEditorDefaultValue(this.position.task) &&
          this.position.task.length >= 20,
      },
      {
        name: '자격요건',
        isChecked:
          !!this.position.qualification &&
          !checkEditorDefaultValue(this.position.qualification) &&
          this.position.qualification.length >= 20,
      },
      {
        name: '우대사항',
        isChecked:
          !!this.position.preferred &&
          !checkEditorDefaultValue(this.position.preferred) &&
          this.position.preferred.length >= 20,
      },
      {
        name: '근무지',
        isChecked:
          (this.position.isSameLocation ||
            (!this.position.isSameLocation &&
              !!this.position.location &&
              !!this.position.address)) &&
          !!this.position.remoteWorkPreference,
      },
      {
        name: '예상 연봉',
        isChecked:
          !!this.position.stockOption &&
          (this.position.minDesiredSalary !== 0 ||
            (this.position.maxDesiredSalary !== 0 &&
              this.position.maxDesiredSalary !== DEFAULT_MAX_SALARY)),
      },
    ]
  }

  setDefaultInfo(positions: DashboardPosition[]) {
    const stockOption = positions.find(
      (position) => !!position.stockOption,
    )?.stockOption
    const expectationKeywords = positions.find(
      (position) => !!position.expectationKeywords.length,
    )?.expectationKeywords
    const hiringProcess = positions.find(
      (position) => position.hiringProcess && !!position.hiringProcess.trim(),
    )?.hiringProcess
    if (expectationKeywords) this.setExpectationKeywords(expectationKeywords)
    if (hiringProcess) this.setHiringProcess(hiringProcess)
    if (stockOption) this.setStockOption(stockOption)
  }

  setIsSameLocation(v: boolean) {
    this.position.isSameLocation = v
  }

  setLocation(v: WithIdNameParentId | null) {
    this.position.location = v
  }

  setInternPeriod(v: number | null) {
    this.position.internPeriod = v
  }

  setStockOption(v: StartupPositionStockOptionType) {
    this.position.stockOption = v
  }

  setRemoteWorkPreference(v: StartupPositionRemoteWorkType) {
    this.position.remoteWorkPreference = v
  }

  setMaxNegotiableSalary(v: number) {
    this.position.maxNegotiableSalary = v
  }

  setAddress(v: string) {
    this.position.address = v
  }

  setJobInterestLevel(v: number | null) {
    this.position.jobInterestLevel = v
  }

  setExpectationKeywords(expectationKeywords: WithIdName[]) {
    this.position.expectationKeywords = expectationKeywords
  }

  setIsMobilePositionListView(v: boolean) {
    this.isMobilePositionListView = v
  }

  setIsOpenTossModal(v: boolean) {
    this.isOpenTossModal = v
  }

  setIsOpenLockModal(v: boolean) {
    this.isOpenLockModal = v
  }

  setIsOpenPositionPreviewModal(v: boolean) {
    this.isOpenPositionPreviewModal = v
  }

  setIsOpenAttentionTipBox(v: boolean) {
    this.isOpenAttentionTipBox = v
  }

  openPositionAdModal(position?: DashboardPosition) {
    this.isOpenAdModal = true
    if (position) this.positionToPublish = position
  }

  closePositionAdModal() {
    this.isOpenAdModal = false
    this.positionToPublish = undefined // 초기화 필요
  }

  offDataChanged() {
    if (!this.hasChanged) return
    this.hasChanged = false
  }

  onDataChanged() {
    if (this.hasChanged) return
    this.hasChanged = true
  }

  setName(v: string) {
    this.position.name = v
  }

  setPositionTypes(v: PositionType[]) {
    this.position.positionTypes = v
  }

  togglePositionType(p: PositionType) {
    const index = findIndex(this.position.positionTypes, (_p) => _p.id === p.id)
    if (index >= 0) {
      this.position.positionTypes.splice(index, 1)
    } else {
      this.position.positionTypes.push(p)
    }
  }

  setCareerType(v: WithIdName) {
    this.position.careerType = v
  }

  setMinDesiredSalary(v: number) {
    this.position.minDesiredSalary = v
  }

  setMaxDesiredSalary(v: number) {
    this.position.maxDesiredSalary = v
  }

  setIsSalaryUnknown(v: boolean) {
    this.isSalaryUnknown = v
  }

  setExperienceRange(v: Range) {
    this.position.experienceRange = v
  }

  setTechStack(v: TechStack) {
    if (this.techStacksId.includes(v.id)) {
      this.position.techStacks = this.position.techStacks.filter(
        (item) => item.id !== v.id,
      )
    } else this.position.techStacks.push(v)
  }

  deleteTechStack(v: TechStack) {
    this.position.techStacks = this.position.techStacks.filter(
      (item) => item !== v,
    )
  }

  setHasSupervisor(v: boolean) {
    this.position.hasSupervisor = v
  }

  setTask(task: string) {
    this.position.task = task
  }

  setQualification(qualification: string) {
    this.position.qualification = qualification
  }

  setPreferred(preferred: string) {
    this.position.preferred = preferred
  }

  setHiringProcess(hiringProcess: string) {
    this.position.hiringProcess = hiringProcess
  }

  toggleExpectationKeyword(keyword: WithIdName) {
    if (this.position.expectationKeywords.find((x) => x.id === keyword.id))
      this.position.expectationKeywords =
        this.position.expectationKeywords.filter((x) => x.id !== keyword.id)
    else {
      if (this.position.expectationKeywords.length > 9)
        return alert('10개까지만 선택할 수 있어요.')
      this.position.expectationKeywords.push(keyword)
    }
  }

  setPosition(v: DashboardPosition) {
    this.position = v
  }

  resetPosition() {
    this.position = initialPosition
    this.resetSalaryOptions()
  }

  resetSalaryOptions() {
    this.hasSalaryUnknownOption = false
    this.isSalaryUnknown = false
  }

  setIsCreating(v: boolean) {
    this.isCreating = v
  }

  setIsPublished(bool: boolean) {
    this.position.isPublished = bool
  }

  checkValidity({
    position, // 사이드박스에서 공개할 때 position 데이터를 파라미터로 받음
    isFakeLogin,
  }: {
    position?: DashboardPosition
    isFakeLogin?: boolean
  }) {
    const p = position || this.position
    const shouldSkipSalary = position ? false : this.isSalaryUnknown //  "예상 연봉 알 수 없어요"는 현재 수정중인 포지션에 대해서만 가능. 사이드박스에서 공개할 경우, 고려하지 않음
    try {
      validateDashboardPosition(p, shouldSkipSalary, !!isFakeLogin)
    } catch (e) {
      if (
        e === DASHBOARD_POSITION_SALARY_VALIDATION_ERROR_MESSAGE &&
        !position
      ) {
        //  예상 연봉만 입력하지 않고 저장해서 에러가 난 경우, "예상 연봉을 알 수 없어요" 옵션을 추가해줌
        this.hasSalaryUnknownOption = true
        scrollIntoView(DASHBOARD_POSITION_FORM_SALARY_SECTION_ID)
      }
      throw e
    }
  }

  async positionBoxSelectHandler(position: DashboardPosition) {
    if (this.position.id === position.id) return
    if (this.confirmDataChanged) return
    this.setPosition(position)
    this.resetSalaryOptions()
    this.setIsCreating(false)
    this.offDataChanged()
  }

  addBoxSelectHandler() {
    if (this.confirmDataChanged) return
    this.resetPosition()
    this.setIsCreating(true)
    this.offDataChanged()
    this.setIsMobilePositionListView(false)
  }

  async savePositionHandler(isFakeLogin?: boolean) {
    try {
      if (this.position.isPublished) this.checkValidity({ isFakeLogin })
      if (
        this.position.isPublished &&
        !window.confirm('공고를 저장하시겠습니까?')
      )
        return
      let hasDetected = false
      await requestToast(
        this.save().then((res) => {
          hasDetected = noticePrivateInfoDetected(res.data, '채용 공고')
        }),
        {
          loading: '저장중',
          success: `공고가 ${
            this.position.isPublished ? '저장' : '임시 저장'
          }되었습니다.`,
        },
      )
      if (!this.position.isPublished)
        customToast('저장된 채용 공고를 공개해주세요.')
      await this.mutateDashboardPositions().then(({ data }) => {
        if (this.isCreating) {
          data && this.setPosition(data[0])
          this.setIsCreating(false)
        } else if (hasDetected) {
          const updated = data?.find((x) => x.id === this.position.id)
          updated && this.setPosition(updated)
        }
      })
      this.offDataChanged() // 저장 후 무조건 데이터 변경체크 끄기
      return true // 이 함수 실행 결과, 문제없이 저장이 완료되었음을 나타내는 반환값
    } catch (e) {
      alert(e)
    }
  }

  async deletePositionHandler(positionId?: number) {
    try {
      if (!window.confirm(DELETE_ALERT_TEXT)) return
      await requestToast(this.delete(positionId || this.position.id), {
        loading: '삭제중',
        success: '공고가 삭제되었습니다.',
      })
      this.mutateDashboardPositions().then(({ data }) => {
        if (data?.length) {
          this.setPosition(data[0])
        } else {
          this.resetPosition()
          this.setIsCreating(true)
        }
        this.offDataChanged()
      })
    } catch (e) {
      alert(e)
    }
  }

  async saveBeforePublish(position: DashboardPosition) {
    if (!this.hasChanged) return // 변경 사항이 있어야 하고,
    if (position.id !== this.position.id) return // 포지션 박스에서 다른 포지션을 선택한 경우가 아니어야 함
    const res = await this.save()
    const hasDetected = noticePrivateInfoDetected(res.data, '채용 공고')
    if (hasDetected) {
      this.mutateDashboardPositions().then(({ data }) => {
        const updated = data?.find((x) => x.id === this.position.id)
        updated && this.setPosition(updated)
      })
      customToast('삭제된 내용을 검토하신 후 다시 공개해주세요 :)')
      return true
    }
    this.offDataChanged() // 저장 후 무조건 데이터 변경체크 끄기
  }

  preparePublish({
    isPublished,
    position, // 포지션 박스에서 공개할 때는 특정 position 데이터를 파라미터로 받음
    isFakeLogin,
  }: {
    isPublished: boolean
    position?: DashboardPosition
    isFakeLogin?: boolean
  }) {
    try {
      if (!isPublished) return this.setIsOpenLockModal(true)
      this.checkValidity({ position, isFakeLogin }) // NOTE(seulgi): 포지션 박스에서 넘어온 것인지 구분 위해 position 파라미터 받은 것 그대로 전달해야 함
      this.openPositionAdModal(position || this.position)
    } catch (e) {
      alert(e)
    }
  }

  async publishHandler() {
    try {
      const hasDetected = await this.saveBeforePublish(this.getPosition)
      if (hasDetected) return this.closePositionAdModal()
      await this.publishAndMutate(this.getPosition)
    } catch (e) {
      alert(e)
    }
  }

  async requestRecommend() {
    try {
      await postRequestDashboardScoutTalentRecommendRequest({
        positionId: this.getPosition.id,
      })
      throw '매니저에게 인재 추천을 요청했어요.\n채용 공고에 적합한 인재를 추천하고 알림을 보내드릴게요.'
    } catch (e) {
      alert(e)
    }
  }

  async requestPositionAdvertisements(
    params: DashboardPositionAdvertisementsParams[],
  ) {
    try {
      await postDashboardPositionAdvertisements(this.getPosition.id, params)
      throw '선택하신 유료 상품은 이메일로 안내드립니다.'
    } catch (e) {
      alert(e)
    }
  }

  // 포지션 박스에서 공개할 때는 특정 position 데이터를 파라미터로 받음
  async unpublishHandler(position?: DashboardPosition) {
    try {
      const _position = position || this.position
      if (!window.confirm(`공고를 비공개하시겠어요?`)) return
      await this.saveBeforePublish(_position)
      await this.publishAndMutate(_position)
    } catch (e) {
      alert(e)
    }
  }

  async save() {
    const params: DashboardPositionParams = {
      ...this.position,
      positionTypes: this.position.positionTypes.map((p) => p.id) || null,
      careerType: this.position.careerType?.id || null,
      techStacks: this.techStacksId,
      hiringProcess: checkEditorDefaultValue(this.position.hiringProcess)
        ? ''
        : this.position.hiringProcess,
      expectationKeywords: this.position.expectationKeywords.map((x) => x.id),
      location: this.position.location?.id || null,
    }
    const api = this.isCreating
      ? postRequestDashboardPositions(params)
      : putRequestDashboardPositions(this.position.id, params)
    return await api
  }

  async delete(id: number | undefined) {
    if (!id) return
    return await deleteRequest(`/dashboard/positions/${id}`)
  }

  async publishAndMutate(position: DashboardPosition) {
    const action = position.isPublished ? '비공개' : '공개'
    await requestToast(
      putRequest(`/dashboard/positions/${position.id}/is-published`, {
        value: !position.isPublished,
      }),
      {
        loading: '저장중',
        success: `공고가 ${action}되었습니다.`,
      },
    )
    this.mutateDashboardPositions().then(() => {
      if (position.id === this.position.id) {
        // 사이드박스에서 공개/비공개 설정 시 현재 열려있는 페이지와 같을 경우 store mutation
        this.setIsPublished(!position.isPublished)
      }
    })
  }

  async mutateDashboardPositions() {
    return mutate('/dashboard/positions').then(
      (res) => res as Envelope<DashboardPosition[]>,
    )
  }
}
